import { Component, Input, OnInit, Output } from "@angular/core";
import { HasPermissionService } from "../../../../../employee/service/has-permission/has-permission.service";
import {
  AbstractControl,
  Form,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import {
  RejectedOptions,
  StatusOptions,
  RejectedEpsOptions,
} from "../../../candidate-disabilities/data.constants";
import { calendarES } from "../../../../../../theme/functions/calendarES";
import { User } from "../../../../../employee/class/user";

@Component({
  selector: "app-candidate-disabilities-admin-status",
  templateUrl: "./candidate-disabilities-admin-status.component.html",
  styleUrls: ["./candidate-disabilities-admin-status.component.scss"],
})
export class CandidateDisabilitiesAdminStatusComponent implements OnInit {
  @Input() form: FormGroup;
  @Input() disabilities_status: any[];
  public statusOptions: any[] = StatusOptions;
  public rejectedOptions: any[] = RejectedOptions;
  public rejectedEpsOptions: any[] = RejectedEpsOptions;
  public es: any;
  public user: User;
  public openDropdownIndex: number | null = null;
  // Nuevo: Array para trackear qué registros son precargados
  private preloadedRecords: boolean[] = [];

  constructor(
    public fb: FormBuilder,
    public hasPermissionService: HasPermissionService,
    public translate: TranslateService,
  ) {
    this.es = calendarES();
    this.user = JSON.parse(localStorage.getItem("profile"));
  }

  ngOnInit() {
    this.listFieldsFormsArray(this.status_tracking, this.disabilities_status);
  }

  public hasPermission(permission) {
    return this.hasPermissionService.hasPermission(permission);
  }

  public hasRole(rol) {
    var r: boolean = false;
    var role = JSON.parse(localStorage.getItem("profile")).roles[0].id;
    if (role == rol) {
      r = true;
    }
    return r;
  }

  // Método actualizado: usa el array de tracking en lugar del índice
  public isPreloadedRecord(index: number): boolean {
    return this.preloadedRecords[index] === true;
  }

  // Método actualizado: verifica si un campo específico puede ser editado
  public canEditField(index: number, permission: string): boolean {
    // Si es un registro precargado, no se puede editar
    if (this.isPreloadedRecord(index)) {
      return false;
    }
    // Si es un registro nuevo, verificar permisos
    return this.hasPermission(permission);
  }

  clearFormArray = (formArray: FormArray) => {
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
    // También limpiar el array de tracking
    this.preloadedRecords = [];
  };

  listFieldsFormsArray(formArrayLoc: FormArray, data: any[]) {
    // NO limpiar el FormArray si ya tiene datos
    // Solo procesar si el FormArray está vacío o si hay datos para cargar
    if (formArrayLoc.length === 0 && data && data.length > 0) {
      data.forEach((element, index) => {
        // Marcar como precargado en nuestro array de tracking
        this.preloadedRecords.push(true);
        this.addStatusTracking(element, true);
      });
    } else if (formArrayLoc.length > 0) {
      // Si ya hay datos en el FormArray, marcarlos todos como precargados
      for (let i = 0; i < formArrayLoc.length; i++) {
        this.preloadedRecords.push(true);
      }
    }
  }

  get status_tracking() {
    return this.form.controls["status_tracking"] as FormArray;
  }

  addStatusTracking(data: any = null, isPreloaded: boolean = false) {
    // Si no es precargado y se está agregando manualmente, agregar false al tracking
    if (!isPreloaded) {
      this.preloadedRecords.push(false);
    }

    data = data || {
      id: undefined,
      candidate_disabilities_id: "",
      status_id: "",
      created_by_user_id: this.user.id,
      rejected_id: "",
      rejected_eps_id: "",
      date_radicated: null,
      paid_date: null,
      received_amount: "0",
      days_paid: null,
      ibc: "0",
      filing_number: null,
      pqr_number: null,
      created_at: null,
    };

    if (data.received_amount != null && !isNaN(data.received_amount))
      data.received_amount = data.received_amount.toLocaleString("en-US");

    const rejected_mandatory = data.status_id == 3 ? true : false;
    const date_radicated_mandatory =
      data.status_id == 4 || data.status_id == 3 ? true : false;
    const paid_mandatory = data.status_id == 5 ? true : false;
    const rejected_eps_mandatory = data.status_id == 6 ? true : false;
    const status_paid_mandory = data.status_id == 12 ? true : false;

    const status_trackingForm = this.fb.group({
      id: [data.id],
      candidate_disabilities_id: [data.candidate_disabilities_id],
      status_id: [data.status_id, Validators.compose([Validators.required])],
      created_by_user_id: [data.created_by_user_id],
      rejected_id: [
        data.rejected_id,
        rejected_mandatory ? Validators.compose([Validators.required]) : [],
      ],
      rejected_eps_id: [
        data.rejected_eps_id,
        rejected_eps_mandatory ? Validators.compose([Validators.required]) : [],
      ],
      date_radicated: [
        data.date_radicated ? new Date(data.date_radicated) : null,
        date_radicated_mandatory
          ? Validators.compose([Validators.required])
          : [],
      ],
      paid_date: [
        data.paid_date ? new Date(data.paid_date) : null,
        paid_mandatory ? Validators.compose([Validators.required]) : [],
      ],
      received_amount: [
        data.received_amount,
        paid_mandatory
          ? Validators.compose([
              Validators.required,
              Validators.pattern("^[0-9.,]*$"),
            ])
          : [],
      ],
      days_paid: [
        data.days_paid,
        status_paid_mandory
          ? Validators.compose([Validators.required, Validators.minLength(1)])
          : [],
      ],
      ibc: [
        data.ibc,
        status_paid_mandory
          ? Validators.compose([
              Validators.required,
              Validators.pattern("^[0-9.,]*$"),
            ])
          : [],
      ],
      filing_number: [data.filing_number],
      pqr_number: [data.pqr_number],
      created_at: [data.created_at ? new Date(data.created_at) : null],
    });

    // Si es un registro precargado, deshabilitar todos los campos
    if (isPreloaded) {
      Object.keys(status_trackingForm.controls).forEach((key) => {
        status_trackingForm.controls[key].disable();
      });
    } else {
      // Si es un registro nuevo, aplicar permisos normalmente
      if (!this.hasPermission("edit status disability")) {
        status_trackingForm.controls["status_id"].disable();
      }
      if (!this.hasPermission("edit rejected disability")) {
        status_trackingForm.controls["rejected_id"].disable();
      }
      if (!this.hasPermission("edit rejected_eps disability")) {
        status_trackingForm.controls["rejected_eps_id"].disable();
      }
      if (!this.hasPermission("edit date_radicated disability")) {
        status_trackingForm.controls["date_radicated"].disable();
      }
      if (!this.hasPermission("edit paid_date disability")) {
        status_trackingForm.controls["paid_date"].disable();
      }
      if (!this.hasPermission("edit received_amount disability")) {
        status_trackingForm.controls["received_amount"].disable();
      }
      if (!this.hasPermission("edit days_paid disability")) {
        status_trackingForm.controls["days_paid"].disable();
      }
      if (!this.hasPermission("edit ibc disability")) {
        status_trackingForm.controls["ibc"].disable();
      }
      if (!this.hasPermission("edit filing_number disability")) {
        status_trackingForm.controls["filing_number"].disable();
      }
      if (!this.hasPermission("edit pqr_number disability")) {
        status_trackingForm.controls["pqr_number"].disable();
      }
    }

    this.status_tracking.push(status_trackingForm);
    return;
  }

  deleteStatusTracking(statusTrackingIndex: number) {
    // Solo permitir eliminar registros nuevos (no precargados)
    if (!this.isPreloadedRecord(statusTrackingIndex)) {
      this.status_tracking.removeAt(statusTrackingIndex);
      // Importante: también eliminar del array de tracking
      this.preloadedRecords.splice(statusTrackingIndex, 1);
    } else {
      // Si intenta eliminar un registro precargado, no hacer nada
      console.warn("No se pueden eliminar registros existentes");
    }
  }

  changeStatus(event: Event, statusTrackingIndex: number): void {
    // No permitir cambios en registros precargados
    if (this.isPreloadedRecord(statusTrackingIndex)) {
      return;
    }

    const itemFormGroup = this.status_tracking.at(
      statusTrackingIndex,
    ) as FormGroup;
    const rejectedControl = itemFormGroup.get("rejected_id");
    const rejectedEpsControl = itemFormGroup.get("rejected_eps_id");
    const radicatedControl = itemFormGroup.get("date_radicated");
    const paidDateControl = itemFormGroup.get("paid_date");
    const daysPaidControl = itemFormGroup.get("days_paid");
    const ibcControl = itemFormGroup.get("ibc");
    const receivedAmountControl = itemFormGroup.get("received_amount");
    const change = (event.target as HTMLSelectElement).value;
    var status_for_verify = ["3", "4", "5", "6", "12"];
    if (status_for_verify.includes(change)) {
      if (change == "3") {
        //Estado rechazado
        this.setControlValidators(rejectedControl, [
          Validators.required,
          Validators.minLength(1),
        ]);
        this.setControlValidators(radicatedControl, [
          Validators.required,
          Validators.minLength(1),
        ]);
        this.setControlValidators(rejectedEpsControl, []);
        this.setControlValidators(paidDateControl, []);
        this.setControlValidators(receivedAmountControl, []);
      } else if (change == "4") {
        //Estado radicado EPS
        this.setControlValidators(radicatedControl, [
          Validators.required,
          Validators.minLength(1),
        ]);
        this.setControlValidators(rejectedControl, []);
        this.setControlValidators(rejectedEpsControl, []);
        this.setControlValidators(paidDateControl, []);
        this.setControlValidators(receivedAmountControl, []);
      } else if (change == "5") {
        //Estado pagado por EPS
        this.setControlValidators(paidDateControl, [Validators.required]);
        this.setControlValidators(receivedAmountControl, [
          Validators.required,
          Validators.pattern("^[0-9.,]*$"),
          Validators.minLength(1),
        ]);
        this.setControlValidators(radicatedControl, []);
        this.setControlValidators(rejectedControl, []);
        this.setControlValidators(rejectedEpsControl, []);
      } else if (change == "6") {
        //Denegado por EPS
        this.setControlValidators(rejectedEpsControl, [Validators.required]);
        this.setControlValidators(radicatedControl, []);
        this.setControlValidators(rejectedControl, []);
        this.setControlValidators(paidDateControl, []);
        this.setControlValidators(receivedAmountControl, []);
      } else if (change == "12") {
        //Estado pagado por Pagada
        this.setControlValidators(paidDateControl, [Validators.required]);
        this.setControlValidators(daysPaidControl, [
          Validators.required,
          Validators.minLength(1),
        ]);
        this.setControlValidators(ibcControl, [
          Validators.required,
          Validators.pattern("^[0-9.,]*$"),
          Validators.minLength(1),
        ]);
        this.setControlValidators(receivedAmountControl, []);
        this.setControlValidators(radicatedControl, []);
        this.setControlValidators(rejectedControl, []);
        this.setControlValidators(rejectedEpsControl, []);
      }
    } else {
      this.setControlValidators(rejectedControl, []);
      this.setControlValidators(radicatedControl, []);
      this.setControlValidators(rejectedEpsControl, []);
      this.setControlValidators(paidDateControl, []);
      this.setControlValidators(receivedAmountControl, []);
    }
  }

  private setControlValidators(
    control: AbstractControl,
    validators: ValidatorFn[],
  ): void {
    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  showControl(statusTrackingIndex: number, statusValue: number): boolean {
    const itemFormGroup = this.status_tracking.at(
      statusTrackingIndex,
    ) as FormGroup;
    return itemFormGroup.get("status_id").value == statusValue;
  }

  formatNumber(statusTrackingIndex: number): void {
    // No permitir formateo en registros precargados
    if (this.isPreloadedRecord(statusTrackingIndex)) {
      return;
    }

    const itemFormGroup = this.status_tracking.at(
      statusTrackingIndex,
    ) as FormGroup;
    const control = itemFormGroup.get("received_amount");
    const controlDays = itemFormGroup.get("days_paid");
    const controlIbc = itemFormGroup.get("ibc");
    if (control.value || controlIbc.value || controlDays.value) {
      // Elimina las comas existentes
      const numericValue = parseFloat(control.value.replace(/,/g, ""));
      if (!isNaN(numericValue)) {
        // Formatea el valor y actualiza el FormControl
        control.setValue(numericValue.toLocaleString("en-US"), {
          emitEvent: false, // Evita ciclos infinitos de eventos
        });
      }
    }
  }

  toggleDropdown(index: number): void {
    if (this.isPreloadedRecord(index)) {
      return;
    }
    this.openDropdownIndex = this.openDropdownIndex === index ? null : index;
  }

  selectOption(formIndex: number, option: any): void {
    const formGroup = this.status_tracking.at(formIndex) as FormGroup;
    formGroup.get("rejected_id").setValue(option.value);
    this.openDropdownIndex = null;
  }

  getSelectedLabel(formIndex: number): string {
    const formGroup = this.status_tracking.at(formIndex) as FormGroup;
    const selectedValue = formGroup.get("rejected_id").value;

    if (!selectedValue) {
      return this.translate.instant("choose");
    }

    const selectedOption = this.rejectedOptions.find(
      (opt) => opt.value == selectedValue,
    );
    return selectedOption
      ? this.translate.instant(selectedOption.label)
      : this.translate.instant("choose");
  }
}



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/modules/disability/components/candidate-disabilities-admin/candidate-disabilities-admin/candidate-disabilities-admin-status/candidate-disabilities-admin-status.component.ts